// components/Footer.js — the one footer, on every page that has room for it.
//
// There used to be two. This one rendered on four routes and only when signed
// out; the landing page carried its own inline copy with a different shape and
// a different set of links. So a signed-in person had no route to the privacy
// policy or the terms from anywhere in the product, and the two public footers
// disagreed about what the site even offered.
//
// What this file no longer does:
//   - inject a <style> tag on every render, holding seven colour literals from
//     the palette the product replaced, a cyan accent that was not the brand
//     teal, and an Orbitron brand face loaded nowhere else. None of it read a
//     token, so none of it followed dark mode.
//   - import FontAwesome and four brand icons that nothing rendered. The
//     social links were removed long ago; the imports stayed, and pulled the
//     icon library into the bundle for nothing.
//   - lay itself out with react-bootstrap Container/Row/Col and an empty
//     spacer column (<Col md={4} className="d-none d-md-block" />).
//
// Links are only ever added here when the page exists. A footer link to a
// redirect, or to a "coming soon", is the cheapest way to look unfinished.
import React from 'react';
import { Link } from 'react-router-dom';
import { IDENTITY, BRAND } from '../copy/product';
import { useAuth } from './AuthContext';
import './Footer.css';

const SUPPORT = 'support@assistalien.com';

// Split in two so the columns are data, not markup, and so a test can assert
// that every destination is a real route.
//
// `publicTo` is where a signed-out visitor goes instead (2026-10-19). Every
// product page sits behind sign-in, so these five links all opened the log-in
// form for someone still deciding what the product is. They open the part of
// the landing page that shows that part, or the pricing page's list of parts
// for the Tracker, which the landing page does not show.
export const FOOTER_PRODUCT = [
  { to: '/jobs', publicTo: '/#jobs', label: 'Jobs' },
  { to: '/job-agent', publicTo: '/#apply', label: 'Auto-apply' },
  { to: '/extension', label: 'Extension' },
  { to: '/design-studio', publicTo: '/#design-studio', label: 'Design Studio' },
  { to: '/interview-coach', publicTo: '/#interview-coach', label: 'Interview coach' },
  { to: '/tracking', publicTo: '/pricing#product', label: 'Tracker' },
  { to: '/pricing', label: 'Pricing' },
];

// Every document a person could reasonably come asking for. The extension
// notice is the privacy policy the Chrome Web Store listing points at; until
// this footer, nothing on the site linked to it at all.
export const FOOTER_LEGAL = [
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/terms-of-service', label: 'Terms of Service' },
  { to: '/security', label: 'Security' },
  { to: '/extension-privacy', label: 'Extension privacy' },
];

const Footer = () => {
  // Outside an AuthProvider (a test, say) this is a signed-out visitor.
  const signedIn = Boolean((useAuth() || {}).currentUser);
  return (
  <footer className="site-footer claude-theme">
    <div className="site-footer-inner">
      <div className="site-footer-brand">
        <span className="site-footer-name claude-serif">{BRAND}</span>
        {/* The same sentence the landing page and the pricing page lead with.
            It comes from copy/product.js so the footer cannot drift into
            describing a different product from the one above it. */}
        <p className="site-footer-line">{IDENTITY}</p>
        <a className="site-footer-mail" href={`mailto:${SUPPORT}`}>
          <span className="material-symbols-rounded" aria-hidden="true">mail</span>
          {SUPPORT}
        </a>
      </div>

      <nav className="site-footer-col" aria-label="Product">
        <h2 className="site-footer-head">Product</h2>
        {FOOTER_PRODUCT.map((l) => (
          <Link key={l.to} to={signedIn ? l.to : (l.publicTo || l.to)} className="site-footer-link">{l.label}</Link>
        ))}
      </nav>

      <nav className="site-footer-col" aria-label="Legal">
        <h2 className="site-footer-head">Legal</h2>
        {FOOTER_LEGAL.map((l) => (
          <Link key={l.to} to={l.to} className="site-footer-link">{l.label}</Link>
        ))}
      </nav>

      <div className="site-footer-base">
        <span>© {new Date().getFullYear()} {BRAND}. All rights reserved.</span>
        <span>Made for people who are tired of typing the same answers.</span>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
